import React from 'react';
import { Box, Container, Typography, Paper, Divider } from '@mui/material';
import { motion } from 'framer-motion';
import { useTheme } from '@mui/material/styles';
import { alpha } from '@mui/material/styles';
import SEO from '../components/common/SEO';
import { pageSEO, generateStructuredData } from '../utils/seo';

const TermsOfServicePage: React.FC = () => {
  const theme = useTheme();
  const seoData = pageSEO['/terms'];
  const structuredData = generateStructuredData('/terms');
  
  return (
    <>
      <SEO 
        pathname="/terms" 
        {...seoData} 
        structuredData={structuredData}
      />
      <Box
        sx={{
        minHeight: '100vh',
        py: 8,
        background: `
          linear-gradient(135deg, 
            ${alpha(theme.palette.primary.main, 0.05)} 0%, 
            ${alpha(theme.palette.secondary.main, 0.05)} 100%
          ),
          radial-gradient(
            circle at 50% 50%,
            ${alpha(theme.palette.primary.light, 0.1)} 0%,
            transparent 50%
          ),
          radial-gradient(
            circle at 0% 100%,
            ${alpha(theme.palette.secondary.light, 0.1)} 0%,
            transparent 50%
          ),
          radial-gradient(
            circle at 100% 0%,
            ${alpha(theme.palette.primary.light, 0.1)} 0%,
            transparent 50%
          )
        `,
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `
            radial-gradient(
              circle at 50% 50%,
              ${alpha(theme.palette.primary.main, 0.03)} 0%,
              transparent 70%
            )
          `,
          animation: 'pulse 8s ease-in-out infinite',
          '@keyframes pulse': {
            '0%': {
              transform: 'scale(1)',
              opacity: 1,
            },
            '50%': {
              transform: 'scale(1.2)',
              opacity: 0.8,
            },
            '100%': {
              transform: 'scale(1)',
              opacity: 1,
            },
          },
        },
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `
            linear-gradient(
              45deg,
              transparent 48%,
              ${alpha(theme.palette.primary.main, 0.03)} 48%,
              ${alpha(theme.palette.primary.main, 0.03)} 52%,
              transparent 52%
            ),
            linear-gradient(
              -45deg,
              transparent 48%,
              ${alpha(theme.palette.primary.main, 0.03)} 48%,
              ${alpha(theme.palette.primary.main, 0.03)} 52%,
              transparent 52%
          )`,
          backgroundSize: '60px 60px',
          opacity: 0.5,
          animation: 'move 20s linear infinite',
          '@keyframes move': {
            '0%': {
              backgroundPosition: '0 0',
            },
            '100%': {
              backgroundPosition: '60px 60px',
            },
          },
          zIndex: 0,
        }}
      />
      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Paper 
            elevation={0} 
            sx={{ 
              p: { xs: 3, md: 6 },
              borderRadius: 3,
              bgcolor: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(10px)',
              boxShadow: '0 10px 30px rgba(0, 0, 0, 0.08)',
            }}
          >
            <Typography variant="h3" component="h1" gutterBottom>
              Terms of Service
            </Typography>
            <Typography variant="subtitle1" color="text.secondary" paragraph>
              Last updated: {new Date().toLocaleDateString()}
            </Typography>
            
            <Divider sx={{ my: 4 }} />

            <Box sx={{ '& > section': { mb: 4 } }}>
              <section>
                <Typography variant="h5" gutterBottom>
                  1. Introduction
            </Typography>
            <Typography paragraph>
                  Welcome to The United Travel. These Terms of Service govern your use of our website and services. 
                  By accessing or using our services, you agree to be bound by these terms.
            </Typography>
              </section>
            
              <section>
                <Typography variant="h5" gutterBottom>
                  2. Services
            </Typography>
            <Typography paragraph>
                  The United Travel provides premium transportation services for corporate clients. Our services include:
            </Typography>
                <ul>
                  <li>Executive car service</li>
                  <li>Airport transfers</li>
                  <li>Corporate event transportation</li>
                  <li>Group transportation</li>
                  <li>Custom transportation solutions</li>
                </ul>
              </section>

              <section>
                <Typography variant="h5" gutterBottom>
                  3. Booking and Cancellation
            </Typography>
            <Typography paragraph>
                  Bookings can be made through our website, mobile app, or by contacting our customer service. 
                  Cancellation policies vary based on the service type and timing:
            </Typography>
                <ul>
                  <li>Standard bookings: 24-hour cancellation notice required</li>
                  <li>Airport transfers: 12-hour cancellation notice required</li>
                  <li>Event transportation: 48-hour cancellation notice required</li>
                </ul>
              </section>

              <section>
                <Typography variant="h5" gutterBottom>
                  4. Payment Terms
            </Typography>
            <Typography paragraph>
                  Payment is required at the time of booking unless prior arrangements have been made. 
                  We accept major credit cards and corporate accounts. All prices are in INR and include applicable taxes.
            </Typography>
              </section>

              <section>
                <Typography variant="h5" gutterBottom>
                  5. Liability
            </Typography>
            <Typography paragraph>
                  The United Travel maintains comprehensive insurance coverage for all vehicles and passengers. 
                  However, we are not liable for delays caused by circumstances beyond our control, including but not limited to:
            </Typography>
                <ul>
                  <li>Traffic conditions</li>
                  <li>Weather conditions</li>
                  <li>Mechanical failures</li>
                  <li>Acts of nature</li>
                </ul>
              </section>

              <section>
                <Typography variant="h5" gutterBottom>
                  6. Privacy
            </Typography>
            <Typography paragraph>
                  We respect your privacy and handle your personal information in accordance with our Privacy Policy. 
                  Please review our Privacy Policy for detailed information about how we collect, use, and protect your data.
            </Typography>
              </section>
            
              <section>
                <Typography variant="h5" gutterBottom>
                  7. Changes to Terms
            </Typography>
            <Typography paragraph>
                  We reserve the right to modify these terms at any time. Changes will be effective immediately upon posting to our website. 
                  Your continued use of our services after changes are posted constitutes acceptance of the modified terms.
            </Typography>
              </section>
            
              <section>
                <Typography variant="h5" gutterBottom>
                  8. Contact Information
            </Typography>
            <Typography paragraph>
                  For questions about these Terms of Service, please contact us at:
            </Typography>
                <Typography>
                  Email: contact.theunitedtravels@gmail.com<br />
                  Phone: +91 6351003667<br />
                  Address: F-8 Plot No. 347/2, Third Floor, Surbhi Complex, Sector - 22, Gandhinagar - 382021
            </Typography>
              </section>
            </Box>
          </Paper>
        </motion.div>
      </Container>
    </Box>
    </>
  );
};

export default TermsOfServicePage; 